import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, CreditCard, Sparkles, Gift, Award, Star } from "lucide-react";
import api, { mediaUrl, imgFallback } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useSettings } from "@/context/SettingsContext";
import { useCategories } from "@/context/CategoriesContext";

function SectionHeader({ kicker, title, to }) {
  return (
    <div className="flex items-end justify-between mb-7 gap-4">
      <div>
        {kicker && <div className="font-mono-label text-[11px] text-[var(--mint-700)] mb-2">{kicker}</div>}
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 inline-block">
          {title}
          <span className="block h-1.5 w-14 bg-[var(--mint-600)] rounded-full mt-2.5" />
        </h2>
      </div>
      {to && (
        <Link to={to} className="btn-outline-coral hidden sm:inline-flex items-center gap-1.5 px-5 py-2.5 font-bold text-sm shrink-0 hover:gap-2.5 transition-all">
          Voir tout <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

// Horizontal snapping carousel of product cards
function Row({ products }) {
  return (
    <div className="flex gap-4 sm:gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-3 -mx-1 px-1">
      {products.map((p) => (
        <div key={p.id} className="snap-start shrink-0 w-[47%] sm:w-[260px]">
          <ProductCard product={p} />
        </div>
      ))}
    </div>
  );
}

function Grid({ products }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
      {products.map((p) => <ProductCard key={p.id} product={p} />)}
    </div>
  );
}

export default function Home() {
  const { settings } = useSettings();
  const { categories } = useCategories();
  const [promos, setPromos] = useState([]);
  const [news, setNews] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [brands, setBrands] = useState([]);
  const [blog, setBlog] = useState([]);

  useEffect(() => {
    api.get("/products?on_promo=1&limit=8").then((r) => setPromos(r.data));
    api.get("/products?is_new=1&limit=8").then((r) => setNews(r.data));
    api.get("/products?featured=1&limit=8").then((r) => setFeatured(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
    api.get("/blog").then((r) => setBlog(r.data.slice(0, 3)));
  }, []);

  return (
    <div className="animate-fade-up">
      {/* ===== HERO — filled boutique banner ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10">
        <div className="relative overflow-hidden rounded-[2rem] sm:rounded-[2.75rem] bg-gradient-to-br from-[var(--mint-100)] via-[var(--mint-50)] to-[var(--mint-100)] cadix-dots px-6 sm:px-12 py-10 sm:py-14">
          {/* decorative floaty blobs */}
          <span className="pointer-events-none absolute -top-10 -left-10 w-44 h-44 rounded-full bg-[var(--mint-400)]/25 blur-2xl animate-floaty" />
          <span className="pointer-events-none absolute -bottom-12 right-1/3 w-56 h-56 rounded-full bg-[var(--mint-500)]/20 blur-2xl animate-floaty" style={{ animationDelay: "1.5s" }} />

          <div className="relative z-10 grid lg:grid-cols-2 gap-8 items-center">
            {/* Left: text */}
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 backdrop-blur text-[var(--mint-700)] text-xs font-extrabold font-mono-label mb-6 shadow-sm">
                <Sparkles className="w-3.5 h-3.5" /> Parapharmacie · Algérie
              </span>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl text-slate-900 leading-[1.05]">
                {settings.hero_title || "Prenez soin de votre peau & santé au meilleur prix"}
              </h1>
              <p className="text-slate-600 text-base sm:text-lg mt-6 max-w-lg">{settings.hero_subtitle}</p>
              <div className="flex flex-wrap gap-3 mt-9">
                <Link to="/catalogue?on_promo=1" data-testid="hero-cta-promos" className="btn-coral px-7 py-3.5 font-bold text-[15px]">Découvrir les promos</Link>
                <Link to="/catalogue" className="btn-outline-coral px-7 py-3.5 font-bold text-[15px]">Tout le catalogue</Link>
              </div>
            </div>

            {/* Right: filled image with floating badge + accents */}
            <div className="relative hidden lg:block">
              <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-[var(--mint-900)]/25 aspect-[4/3] border-4 border-white">
                <img
                  src={settings.hero_image ? mediaUrl(settings.hero_image) : "https://images.unsplash.com/photo-1556228720-195a672e8a03?w=900&q=80"}
                  alt="Soins & cosmétiques Cadix" onError={imgFallback}
                  className="w-full h-full object-cover" />
              </div>
              {/* floating -50% badge */}
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 animate-floaty">
                <div className="text-[11px] text-slate-400 font-mono-label">Jusqu'à</div>
                <div className="font-display font-extrabold text-3xl text-[var(--mint-600)] leading-none">-50%</div>
                <div className="text-[11px] text-slate-500 mt-0.5">sur une sélection</div>
              </div>
              {/* floating turquoise pill */}
              <div className="absolute -top-4 -right-2 bg-gradient-to-r from-[var(--mint-600)] to-[var(--mint-500)] text-white rounded-full px-4 py-2 shadow-lg animate-floaty flex items-center gap-2" style={{ animationDelay: "1s" }}>
                <Truck className="w-4 h-4" /> <span className="text-sm font-bold">Livraison 24–48h</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Category chips strip ===== */}
      {categories.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-9" data-testid="section-category-chips">
          <div className="flex gap-5 sm:gap-7 overflow-x-auto no-scrollbar pb-2">
            {categories.map((c) => (
              <Link key={c.id} to={`/categorie/${c.id}`} data-testid={`home-chip-${c.id}`} className="group shrink-0 flex flex-col items-center gap-2.5 w-[76px] sm:w-[92px]">
                <span className="chip-cat w-[68px] h-[68px] sm:w-20 sm:h-20 rounded-full overflow-hidden grid place-items-center">
                  <img src={mediaUrl(c.image)} alt={c.label} onError={imgFallback} className="w-full h-full object-cover rounded-full group-hover:scale-110 transition-transform duration-500" />
                </span>
                <span className="text-[11px] sm:text-xs font-bold text-slate-700 text-center leading-tight">{c.label}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ===== Trust band ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-10">
        <div className="rounded-[1.75rem] bg-gradient-to-r from-[var(--mint-600)] to-[var(--mint-400)] text-white px-5 sm:px-10 py-6 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 shadow-lg shadow-[var(--mint-600)]/25">
          {[[Truck, "Livraison partout", "Dans les 58 wilayas d'Algérie"], [CreditCard, "Paiement à la livraison", "Payez en toute confiance"], [ShieldCheck, "100% Original", "Produits authentiques certifiés"]].map(([Icon, t, s], i) => (
            <div key={i} className="flex items-center gap-3.5 justify-center sm:justify-start">
              <span className="w-11 h-11 rounded-2xl bg-white/20 grid place-items-center shrink-0"><Icon className="w-5 h-5" /></span>
              <div className="leading-tight">
                <div className="font-display font-bold text-lg">{t}</div>
                <div className="text-white/85 text-[13px]">{s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-16 sm:space-y-20 py-14 sm:py-16">
        {/* Offers — carousel */}
        {promos.length > 0 && (
          <section data-testid="section-offres">
            <SectionHeader kicker="ÉCONOMISEZ MAINTENANT" title="Nos Offres" to="/catalogue?on_promo=1" />
            <Row products={promos} />
          </section>
        )}

        {/* Promo banners: Loyalty (coral) + Gift card (turquoise) */}
        <section className="grid md:grid-cols-2 gap-5" data-testid="section-promo-banners">
          <Link to="/fidelite" data-testid="banner-loyalty" className="group relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[var(--mint-600)] to-[var(--mint-400)] text-white p-8 min-h-[210px] flex flex-col justify-center shadow-lg shadow-[var(--mint-600)]/25 hover:-translate-y-1 transition-transform">
            <Award className="absolute -right-5 -top-5 w-40 h-40 text-white/10 rotate-12" />
            <span className="inline-flex items-center gap-2 text-xs font-mono-label bg-white/20 px-3 py-1 rounded-full w-fit mb-3"><Sparkles className="w-3.5 h-3.5" /> Programme de fidélité</span>
            <h3 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight relative z-10">Gagnez des points<br />à chaque commande</h3>
            <p className="text-white/90 mt-2 relative z-10 max-w-md text-sm">Cumulez des points, montez en statut BRONZE, Silver et Gold, et échangez-les contre des récompenses.</p>
            <span className="mt-4 inline-flex items-center gap-1.5 font-bold relative z-10 group-hover:gap-3 transition-all">Découvrir le programme <ArrowRight className="w-4 h-4" /></span>
          </Link>
          <Link to="/carte-cadeau" data-testid="banner-giftcard" className="group relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[var(--mint-600)] to-[var(--mint-500)] text-white p-8 min-h-[210px] flex flex-col justify-center shadow-lg shadow-[var(--mint-600)]/25 hover:-translate-y-1 transition-transform">
            <Gift className="absolute -right-5 -top-5 w-40 h-40 text-white/10 -rotate-12" />
            <span className="inline-flex items-center gap-2 text-xs font-mono-label bg-white/20 px-3 py-1 rounded-full w-fit mb-3"><CreditCard className="w-3.5 h-3.5" /> Cartes cadeaux</span>
            <h3 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight relative z-10">Offrez une<br />carte cadeau Cadix</h3>
            <p className="text-white/90 mt-2 relative z-10 max-w-md text-sm">Le cadeau idéal pour vos proches : montant au choix, livraison partout en Algérie.</p>
            <span className="mt-4 inline-flex items-center gap-1.5 font-bold relative z-10 group-hover:gap-3 transition-all">Commander une carte <ArrowRight className="w-4 h-4" /></span>
          </Link>
        </section>

        {/* New arrivals — grid */}
        {news.length > 0 && (
          <section data-testid="section-nouveautes">
            <SectionHeader kicker="TOUT NOUVEAU" title="Nouvel Arrivage" to="/catalogue?is_new=1" />
            <Grid products={news} />
          </section>
        )}

        {/* Categories — rounded tiles */}
        {categories.length > 0 && (
          <section data-testid="section-categories">
            <SectionHeader kicker="EXPLOREZ" title="Nos Rayons" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5">
              {categories.slice(0, 8).map((c) => (
                <Link key={c.id} to={`/categorie/${c.id}`} data-testid={`home-category-${c.id}`}
                  className="group relative aspect-[4/5] sm:aspect-square rounded-[1.5rem] overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all">
                  <img src={mediaUrl(c.image)} alt={c.label} onError={imgFallback} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--mint-900)]/80 via-[var(--mint-900)]/15 to-transparent" />
                  <span className="absolute bottom-3.5 left-3.5 right-3.5 font-display font-bold text-white text-base sm:text-lg leading-tight">{c.label}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Coups de coeur — carousel */}
        {featured.length > 0 && (
          <section data-testid="section-coups-de-coeur">
            <SectionHeader kicker="APPROUVÉ PAR NOS PHARMACIENS" title="Nos Coups de Cœur" to="/catalogue?featured=1" />
            <Row products={featured} />
          </section>
        )}

        {/* Brands — marquee strip */}
        {brands.length > 0 && (
          <section data-testid="section-marques">
            <SectionHeader kicker="ILS NOUS FONT CONFIANCE" title="Nos Marques" to="/marques" />
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
              {brands.slice(0, 10).map((b) => (
                <Link key={b.id} to={`/marque/${b.id}`} data-testid={`home-brand-${b.id}`}
                  className="aspect-[3/2] rounded-[1.25rem] bg-white border border-[#F5AC94]/40 hover:border-[var(--mint-600)] hover:shadow-md hover:-translate-y-1 grid place-items-center p-4 transition-all">
                  {b.logo ? <img src={mediaUrl(b.logo)} alt={b.name} onError={imgFallback} className="max-h-full max-w-full object-contain" /> :
                    <span className="font-display font-bold text-slate-700 text-center text-sm sm:text-base">{b.name}</span>}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Blog */}
        {blog.length > 0 && (
          <section data-testid="section-blog">
            <SectionHeader kicker="LE MAGAZINE" title="Conseils & Astuces" to="/blog" />
            <div className="grid sm:grid-cols-3 gap-5">
              {blog.map((post) => (
                <Link key={post.id} to={`/blog/${post.id}`} className="card-boutique group overflow-hidden">
                  <div className="aspect-[16/10] overflow-hidden bg-mint-50 rounded-t-[1.6rem]">
                    <img src={mediaUrl(post.image)} alt={post.title} onError={imgFallback} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <div className="text-[11px] text-[var(--mint-700)] font-mono-label mb-2 flex items-center gap-1.5"><Star className="w-3 h-3" /> {post.author}</div>
                    <h3 className="font-display font-bold text-lg text-slate-900 leading-snug line-clamp-2 group-hover:text-[var(--mint-700)] transition-colors">{post.title}</h3>
                    <p className="text-sm text-slate-500 mt-2 line-clamp-2">{post.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
